import React, { useEffect } from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import * as Actions from "../../redux/actions";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const serviceColors = [
  { bg: "rgba(255, 192, 1, 0.08)", accent: "#FFC001" },
  { bg: "rgba(0, 200, 150, 0.08)", accent: "#00C896" },
  { bg: "rgba(100, 140, 255, 0.08)", accent: "#648CFF" },
  { bg: "rgba(255, 100, 130, 0.08)", accent: "#FF6482" },
  { bg: "rgba(180, 130, 255, 0.08)", accent: "#B482FF" },
  { bg: "rgba(255, 160, 50, 0.08)", accent: "#FFA032" },
  { bg: "rgba(50, 210, 210, 0.08)", accent: "#32D2D2" },
  { bg: "rgba(255, 200, 100, 0.08)", accent: "#FFC864" },
];

const ServiceHighlights = ({ services, loadServices }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!services || services.length === 0) {
      loadServices();
    }
  }, []);

  if (!services || services.length === 0) return null;

  return (
    <Box
      sx={{
        py: { xs: 5, md: 7 },
        background: "linear-gradient(to bottom, #111 0%, #0d0d0d 100%)",
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "22px", md: "30px" },
                color: "#fff",
              }}
            >
              Services
            </Typography>
            <Typography sx={{ color: "#777", fontSize: "14px", mt: 0.3 }}>
              Professional services from verified providers
            </Typography>
          </Box>
          <Typography
            component={Link}
            to="/services"
            sx={{
              color: "#111",
              fontWeight: 600,
              fontSize: { xs: "12px", md: "14px" },
              textDecoration: "none",
              backgroundColor: "#FFC001",
              px: 2.5,
              py: 0.8,
              borderRadius: "20px",
              "&:hover": { backgroundColor: "#e6ac00" },
            }}
          >
            Browse All
          </Typography>
        </Box>

        {/* Services Grid */}
        <Grid container spacing={2.5}>
          {services.slice(0, 8).map((service, index) => {
            const colors = serviceColors[index % serviceColors.length];
            return (
              <Grid item xs={6} sm={4} md={3} key={service.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.06 }}
                >
                  <Box
                    onClick={() => navigate(`/services/${service.id}`)}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "16px",
                      overflow: "hidden",
                      position: "relative",
                      height: { xs: "130px", md: "160px" },
                      border: "1px solid #2a2a2a",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: colors.accent,
                        transform: "translateY(-4px)",
                        boxShadow: `0 8px 25px ${colors.accent}25`,
                        "& .service-bg": { filter: "blur(6px) brightness(0.35)", transform: "scale(1.08)" },
                        "& .service-arrow": { opacity: 1, transform: "translateX(0)" },
                      },
                    }}
                  >
                    {/* Blurred background */}
                    <Box
                      className="service-bg"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: service.image ? `url(${service.image})` : "none",
                        backgroundColor: service.image ? "transparent" : "#1a1a1a",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        filter: "blur(3px) brightness(0.45)",
                        transform: "scale(1.05)",
                        transition: "all 0.4s ease",
                      }}
                    />
                    {/* Dark gradient overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 60%, transparent 100%)",
                      }}
                    />
                    {/* Accent top line */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0, left: 0, right: 0,
                        height: "3px",
                        backgroundColor: colors.accent,
                        opacity: 0.8,
                      }}
                    />
                    {/* Content */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0, left: 0, right: 0,
                        p: { xs: 1.5, md: 2 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: { xs: "13px", sm: "14px", md: "15px" },
                          textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                        }}
                      >
                        {service.name}
                      </Typography>
                      <ArrowForwardIcon
                        className="service-arrow"
                        sx={{
                          fontSize: 16,
                          color: colors.accent,
                          opacity: 0,
                          transform: "translateX(-8px)",
                          transition: "all 0.3s ease",
                          flexShrink: 0,
                          ml: 1,
                        }}
                      />
                    </Box>
                    {/* Fallback icon if no image */}
                    {!service.image && (
                      <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <MiscellaneousServicesIcon sx={{ fontSize: 40, color: colors.accent, opacity: 0.6 }} />
                      </Box>
                    )}
                  </Box>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

const mapStateToProps = ({ Services }) => ({
  services: Services.services,
});

const mapDispatchToProps = (dispatch) => ({
  loadServices: () => dispatch(Actions.thunkLoadServices()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ServiceHighlights);
